import React, { useEffect, useState } from "react";
import {
  Button,
  Breadcrumb,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Row,
  Stack,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import PubSub from "pubsub-js";
import { regExPattern } from "../../utils/regExPattern";
import { validationMessages } from "../../utils/messages";
import { ROUTES } from "../../constants/webRouting";
import solisPowerApi from "../../api/solisPowerApi";
import { getUserDetails } from "../../utils/helper";
import { handleUppercaseChange } from "../common/UpperCaseMethod";
import PERMISSIONS from "../../constants/permissions";

const CommissionEdit = () => {
  const userInfo = getUserDetails();
  const location = useLocation();
  const navigate = useNavigate();

  const initialValues = {
    amount: "",
    lead_id: null,
    contact_id: null,
    project_id: null,
  };

  const initialErrors = {
    // firstname: { message: validationMessages.name.required },
  };

  const [commission, setCommission] = useState(
    location.state ? location.state : initialValues
  );
  const [errors, setErrors] = useState(
    !location.state ? initialErrors : {}
  );
  const [validated, setValidated] = useState(false);

  // Dropdown data states
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);

  // Load data on mount
  useEffect(() => {
    init();
  }, []);

  // Load commission data in edit mode
  useEffect(() => {
    if (location.pathname.includes("/e") && commission?.id) {
      fetchCommissionData();
    }
  }, [location.pathname, commission?.id]);

  // Fetch projects when lead changes
  useEffect(() => {
    if (commission?.lead_id) {
      solisPowerApi.fetchLeadProjectLineItems(commission.lead_id).then((rows) => {
        if (Array.isArray(rows)) {
          setProjects(
            rows.map((proj) => ({
              value: proj.id,
              label: proj.site_name || "Untitled Project",
            }))
          );
        }
      });
    } else {
      setProjects([]);
    }
  }, [commission?.lead_id]);

  async function init() {
    // Fetch Leads
    if (
      [PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_LEAD].some((permission) => {
        return userInfo?.permissions.map((p) => p.name).includes(permission);
      })
    ) {
      const fetchedLeads = await solisPowerApi.fetchLeads();
      if (Array.isArray(fetchedLeads)) {
        setLeads(
          fetchedLeads.map((lead) => ({
            value: lead.id,
            label: lead.lead_no
              ? `${lead.lead_no} - ${lead.lead_name || ""}`
              : lead.lead_name || "",
          }))
        );
      }
    }

    // Fetch Contacts
    if (
      [PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_CONTACT].some((permission) => {
        return userInfo?.permissions.map((p) => p.name).includes(permission);
      })
    ) {
      const fetchedContacts = await solisPowerApi.fetchContactsForEnquiry();
      if (Array.isArray(fetchedContacts)) {
        setContacts(
          fetchedContacts.map((contact) => ({
            value: contact.id,
            label: `${contact.firstname || ""} ${contact.lastname || ""}`.trim(),
          }))
        );
      }
    }
  }

  async function fetchCommissionData() {
    if (commission?.id) {
      const result = await solisPowerApi.fetchCommissionById(commission.id);
      if (result) {
        setCommission(result);
      }
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    // // Validation for firstname
    // if (name === "firstname") {
    //   if (value === "") {
    //     setErrors((prev) => ({
    //       ...prev,
    //       [name]: { message: validationMessages.name.required },
    //     }));
    //   } else if (
    //     e?.target?.pattern &&
    //     !RegExp(e.target.pattern).test(value)
    //   ) {
    //     setErrors((prev) => ({
    //       ...prev,
    //       [name]: { message: validationMessages.name.invalid },
    //     }));
    //   } else {
    //     setErrors((prev) => {
    //       const newErrors = { ...prev };
    //       delete newErrors[name];
    //       return newErrors;
    //     });
    //   }
    // }

    // setValidated(true);
    setCommission((prev) => ({ ...prev, [name]: value }));
  };

  const checkRequiredFields = () => {
    let isError = false;

    // Validate firstname
    if (!commission?.firstname || commission?.firstname === "") {
      setErrors((prev) => ({
        ...prev,
        firstname: { message: validationMessages.name.required },
      }));
      isError = true;
    } else if (
      regExPattern?.name &&
      !RegExp(regExPattern.name).test(commission.firstname)
    ) {
      setErrors((prev) => ({
        ...prev,
        firstname: { message: validationMessages.name.invalid },
      }));
      isError = true;
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors["firstname"];
        return newErrors;
      });
    }

    return isError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    // if (checkRequiredFields()) {
    //   return;
    // }

    const record = { ...commission };

    // Clean up the record
    delete record.lead_name;
    delete record.contact_name;
    delete record.project_name;
    delete record.createdbyname;
    delete record.lastmodifiedbyname;

    // Handle amount - convert to number or null
    if (record.amount === "" || record.amount === null) {
      record.amount = null;
    } else {
      record.amount = parseFloat(record.amount) || null;
    }

    // Handle lead_id and contact_id - ensure they're UUIDs or null
    if (!record.lead_id || record.lead_id === "") {
      record.lead_id = null;
    }
    if (!record.contact_id || record.contact_id === "") {
      record.contact_id = null;
    }
    if (!record.project_id || record.project_id === "") {
      record.project_id = null;
    }

    // Create or Update
    if (record.id) {
      // Update
      const result = await solisPowerApi.saveCommission(record);
      if (result?.success) {
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Record Update",
          message: "Commission updated successfully",
        });
        navigate(`${ROUTES.COMMISSION.VIEW.replace(":id", record.id)}`, {
          state: record,
        });
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Record Update Error",
          message: result?.errors || "Failed to update commission",
        });
      }
    } else {
      // Create
      const result = await solisPowerApi.createCommission(record);
      if (result?.success) {
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Record Created",
          message: "Commission created successfully",
        });
        navigate(
          `${ROUTES.COMMISSION.VIEW.replace(":id", result.id || result.data?.id)}`,
          { state: result.data || result }
        );
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Record Save Error",
          message: result?.errors || "Failed to create commission",
        });
      }
    }
  };

  const handleCancel = () => {
    if (commission?.id) {
      navigate(`${ROUTES.COMMISSION.VIEW.replace(":id", commission.id)}`, {
        state: commission,
      });
    } else {
      navigate(ROUTES.COMMISSION.LIST);
    }
  };

  // Get selected lead for dropdown
  const selectedLead = commission?.lead_id
    ? leads.find((lead) => lead.value === commission.lead_id)
    : null;

  // Get selected contact for dropdown
  const selectedContact = commission?.contact_id
    ? contacts.find((contact) => contact.value === commission.contact_id)
    : null;

  return (
    <Container className="pb-5">
      <Row>
        <Col md={12} className="sticky-breadcrumb">
          <Breadcrumb>
            <Breadcrumb.Item onClick={() => navigate(ROUTES.HOME)}>
              Home
            </Breadcrumb.Item>
            <Breadcrumb.Item onClick={() => navigate(ROUTES.COMMISSION.LIST)}>
              Commissions
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col md={12}>
          <Form
            className=""
            onSubmit={handleSubmit}
            noValidate
            validated={validated}
          >
            <Card className="shadow">
              <CardHeader className="bg-custom text-white sticky-card-header">
                <Row className="w-100">
                  <Col xs={12} sm={12} md={6} lg={6} className="">
                    <div className="d-flex align-items-center h-100 gap-3">
                      {commission?.id ? (
                        <h5 className="m-0">
                          Edit Commission - {commission?.firstname || ""}
                        </h5>
                      ) : (
                        <h5 className="m-0">Create Commission</h5>
                      )}
                    </div>
                  </Col>
                  <Col xs={12} sm={12} md={6} lg={6} className="p-0">
                    <Stack
                      direction="horizontal"
                      className="float-end float-md-end"
                      gap={2}
                    >
                      <Button
                        size="sm"
                        className="btn-save"
                        type="submit"
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        className="btn-delete"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="">
                <Row lg={12} className="view-form">
                  <Col xs={12} sm={12} md={6} lg={6}>
                    <Form.Group>
                      <Form.Label
                        className="form-view-label"
                        htmlFor="formBasicContact"
                      >
                        Commission Agent Name
                      </Form.Label>
                      <Select
                        placeholder="Select Contact"
                        isClearable
                        options={contacts}
                        value={selectedContact}
                        onChange={(e) => {
                          setCommission((prev) => ({
                            ...prev,
                            contact_id: e ? e.value : null,
                            contact_name: e ? e.label : null,
                          }));
                          setValidated(true);
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors?.contact_id?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={12} md={6} lg={6}>
                    <Form.Group>
                      <Form.Label
                        className="form-view-label"
                        htmlFor="formBasicAmount"
                      >
                        Commission  Amount
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="amount"
                        placeholder="Enter amount"
                        value={commission?.amount || ""}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors?.amount?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col xs={12} sm={12} md={6} lg={6}>
                    <Form.Group>
                      <Form.Label
                        className="form-view-label"
                        htmlFor="formBasicLead"
                      >
                        Lead Name
                      </Form.Label>
                      <Select
                        placeholder="Select Lead"
                        isClearable
                        options={leads}
                        value={selectedLead}
                        onChange={(e) => {
                          setCommission((prev) => ({
                            ...prev,
                            lead_id: e ? e.value : null,
                            lead_name: e ? e.label : null,
                            project_id: null,
                            project_name: null,
                          }));
                          setValidated(true);
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors?.lead_id?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col xs={12} sm={12} md={6} lg={6}>
                    <Form.Group>
                      <Form.Label
                        className="form-view-label"
                        htmlFor="formBasicProject"
                      >
                        Project Name
                      </Form.Label>
                      <Select
                        placeholder="Select Project"
                        isClearable
                        options={projects}
                        value={commission?.project_id ? projects.find((p) => p.value === commission.project_id) : null}
                        onChange={(e) => {
                          setCommission((prev) => ({
                            ...prev,
                            project_id: e ? e.value : null,
                            project_name: e ? e.label : null,
                          }));
                          setValidated(true);
                        }}
                      />
                    </Form.Group>
                  </Col>


                </Row>
              </CardBody>
            </Card>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default CommissionEdit;
